import { WatchQueryFetchPolicy } from '@apollo/client'

import { useLazyFetchStats } from 'modules/shift_management/components/ShiftManagement/MainPage/useFetchStats'
import { EnabledFeaturesState } from 'modules/shift_management/hooks/reducers/enabledFeatures'
import { type StatsContent } from 'modules/shift_management/hooks/reducers/schedule'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import Api from 'modules/shift_management/lib/Api'
import type { Filters } from 'modules/shift_management/lib/types'
import type { ShiftsRow, View } from 'modules/shift_management/lib/types'
import ShiftsConflicts from 'modules/shift_management/store/internal/collections/ShiftsConflicts'

interface Result {
  shiftsRows: ShiftsRow[]
  stats: StatsContent
}

/**
 * @deprecated Refactor this to remove the usage of contractVersions.
 * When fetching all the employees that have changed after adding a shift, we
 * might face that we need to refetch more than 1 page of employees. This
 * function takes care of the pagination
 */
const fetchShiftRows = async (
  view: View,
  filters: Filters,
  employeeIds: number[],
  features: EnabledFeaturesState
) => {
  let page = 1
  const { startAt, endAt, groupType } = view
  let shiftsRowsToRefresh: ShiftsRow[] = []
  let result: Result = {
    shiftsRows: [],
    stats: {
      shiftConflicts: new ShiftsConflicts([]),
      hasDrafts: false,
      hasDraftsWithoutConflict: false,
    },
  }

  do {
    // TODO: After adding new shifts, we don't need to fetch leaves, planning_versions,
    // etc of the employees as they are already loaded. In order to avoid it, I would
    // create another method into Api that only fetches /employees_permissions
    const [{ shiftsRows }, { stats }] = await Promise.all([
      Api.fetch({ page, startAt, endAt, filters, groupType, features, employeeIds }),
      Api.fetchStats({ startAt, endAt, filters, view, employeeIds }),
    ])
    const statsResult = {
      hasDrafts: stats.has_drafts,
      hasDraftsWithoutConflict: stats.has_drafts_without_conflict,
      shiftConflicts: ShiftsConflicts.buildFromProps(stats.shift_conflicts || []),
    }
    result = { shiftsRows, stats: statsResult }
    shiftsRowsToRefresh = [...shiftsRowsToRefresh, ...result.shiftsRows]
    page += 1
  } while (result.shiftsRows.length === 30 && view.isByEmployees)

  return { ...result, shiftsRows: shiftsRowsToRefresh }
}

const useRefreshShiftsRows = () => {
  const [, dispatchShiftsRows] = useShiftsRows()
  const [filters] = useFilters()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [features] = useEnabledFeatures()
  const [, dispatchUI] = useUI()

  /**
   * @deprecated Don't use this function.
   * Use the mutations and the proper dispatch to manage shifts, rest days, etc.
   * Also, remember to call refreshStats to update conflicts counters, etc.
   */
  const refreshShiftsRows = async (
    shiftsRows: ShiftsRow[],
    employeeIds: number[] = [],
    targetView?: View
  ) => {
    const employeeIdsFromShiftsRows = shiftsRows.flatMap((row: ShiftsRow) => row.employeeIds)
    const requestEmployeeIds = [...employeeIds, ...employeeIdsFromShiftsRows]
    const { shiftsRows: newShiftsRows, stats } = await fetchShiftRows(
      targetView || view,
      filters,
      requestEmployeeIds,
      features
    )

    dispatchShiftsRows({
      type: 'REFRESH_SHIFTS_ROWS',
      affectedEmployeeIds: requestEmployeeIds,
      shiftsRows: newShiftsRows,
    })
    dispatchUI({ type: 'NOT_REFRESHING_STATS' })
    dispatchSchedule({
      type: 'REFRESH_STATS',
      previousShiftsRows: shiftsRows,
      shiftsRows: newShiftsRows,
      stats,
    })
  }

  return { refreshShiftsRows }
}

const useRefreshStats = ({}: {
  fetchPolicy?: WatchQueryFetchPolicy
} = {}) => {
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()
  const { refetchData } = useLazyFetchStats()

  const refreshStats = async (shiftsRows: ShiftsRow[]) => {
    const { stats } = await refetchData()
    dispatchShiftsRows({
      type: 'REFRESH_SHIFTS_ROWS',
      shiftsRows: [],
      affectedEmployeeIds: [],
    })
    dispatchUI({ type: 'NOT_REFRESHING_STATS' })
    dispatchSchedule({
      type: 'REFRESH_STATS',
      previousShiftsRows: shiftsRows,
      shiftsRows: [],
      stats: {
        hasDrafts: stats.hasDrafts,
        hasDraftsWithoutConflict: stats.hasDraftsWithoutConflict,
        shiftConflicts: stats.shiftConflicts,
      },
    })
  }

  return { refreshStats }
}

export { useRefreshShiftsRows, useRefreshStats }
